import { type PropsWithChildren } from 'react';

import { useTranslate } from '@i18n';

import { u } from '../../styles/unit';
import { Stack } from '../Layout';
import { Heading2, Heading3 } from '../Text';
import * as css from './ActivitySidebar.css';

/**
 * Props for {@link ActivitySidebar}.
 * Renders the sidebar list of activities/swings and the currently selected key parameter label.
 */
type ActivitySidebarProps = {
    /**
     * Display name of the currently selected parameter (e.g. "Club Speed", "Carry", etc).
     * Rendered in the sidebar header.
     */
    selectedKeyParameter: string;

    /**
     * Fallback value to display when an item has no value for the selected parameter.
     * NOTE: Currently not used by {@link ActivitySidebar}; consider wiring it in or removing.
     */
    fallbackDisplayValue: string;
};

/**
 * Props for {@link ActivitySidebarItem}.
 * Represents a single swing/activity row in the sidebar list.
 */
type ActivitySidebarItemProps = {
    /**
     * When true, renders the item in an "analyzing" state (e.g. subdued/animated styling).
     */
    isAnalyzing?: boolean;

    /**
     * Display index/number of the swing/activity.
     */
    number: number;

    /**
     * Display value for the currently selected parameter for this swing/activity.
     */
    value: string;
};
export const ActivitySidebarItem = ({ isAnalyzing, number, value }: ActivitySidebarItemProps) => {
    return (
        <li>
            <span className={css.item({ isAnalyzing })}>
                <span className={css.itemNumber}>
                    <span className={css.itemNumberText}>{number}</span>
                </span>
                <span className={css.itemValue}>{value}</span>
            </span>
        </li>
    );
};

export function ActivitySidebar({ selectedKeyParameter, children }: PropsWithChildren<ActivitySidebarProps>) {
    const t = useTranslate();
    return (
        <div className={css.root}>
            <Stack mb={u(3)}>
                <Heading2 color="muted">{t('activity_navigation.header')}</Heading2>
                <Heading3 className={css.parameterName} transform="uppercase">
                    {selectedKeyParameter}
                </Heading3>
            </Stack>
            <ul className={css.list}>{children}</ul>
        </div>
    );
}
